import React, { useState } from 'react';
import Seo from '../components/Seo';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  IconButton,
  Link,
  Tooltip,
  Modal,
  Fade,
  Backdrop,
  Button,
  Menu,
  MenuItem,
  ListItemIcon as MenuListItemIcon,
  ListItemText as MenuListItemText,
} from '@mui/material';
import {
  Language as LanguageIcon,
  School as SchoolIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { FaWeixin, FaInstagram } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useAppLanguage } from '../hooks/useAppLanguage';

// <<< UPDATED: doctors array with new 'qualifications' property
const doctors = [
  {
    id: 'ivy_yang',
    image: '/img/ivy-photo.jpg',
    linkedin: 'https://www.linkedin.com/in/yi-ivy-yang-dds-8a08698b/',
    wechatQrCode: '/img/ivy-wechat.jpg',
    instagram: '',
    qualifications: [
      {
        src: '/img/logo/aaid.PNG',
      },
      {
        src: '/img/logo/rwc.PNG',
      },
      {
        src: '/img/logo/aaca.PNG',
      },
      {
        src: '/img/logo/rs.PNG',
      },
      
    ],
  },
  {
    id: 'yingying_chi',
    image: '/img/chi-photo.png',
    linkedin: '',
    wechatQrCode: '',
    instagram: '',
    qualifications: [
      {
        src: '/img/logo/ada.jpg',
      },
      {
        src: '/img/logo/cda.png',
      },
      {
        src: '/img/logo/sccds.jpeg',
      },
    ],
  },
  {
    id: 'umi',
    image: '/img/umi.jpg',
    linkedin: '',
    wechatQrCode: '',
    instagram: 'https://www.instagram.com/umi_thecorgi/',
    qualifications: [
    ],
  },
];

interface TranslatedDoctorData {
  name: string;
  role: string;
  languages: string[];
  education: string[];
  detailedDescription: string;
}

const DoctorProfile: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { doctorId } = useParams();
  const { t, currentLanguage, setLanguage } = useAppLanguage();

  const [openWeChatModal, setOpenWeChatModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleOpenWeChatModal = () => setOpenWeChatModal(true);
  const handleCloseWeChatModal = () => setOpenWeChatModal(false);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (language: string) => {
    setLanguage(language);
    handleClose();
  };

  const doctor = doctors.find(d => d.id === doctorId);
  const doctorData = t(`doctorProfile.doctors.${doctorId}`, { returnObjects: true }) as TranslatedDoctorData;

  if (!doctor || !doctorData.name) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h4">{t('doctorProfile.notFoundTitle')}</Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('doctorProfile.notFoundBody')}
        </Typography>
      </Box>
    );
  }

  const iconSize = 35;

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'auto',
    maxWidth: '90%',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    p: { xs: 3, md: 4 },
    textAlign: 'center',
    outline: 'none',
  };

  return (
    <div className="doctor-profile-container">
      <Seo
        title={t('doctorProfile.seo.title', { name: doctorData.name })}
        description={t('doctorProfile.seo.description', { name: doctorData.name })}
        canonicalUrl={`https://www.pacifica.dental/members/${doctor.id}`}
        ogImage={doctor.image}
        ogType="profile"
      />
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="lg">
          {/* <<< UPDATED: The Grid container now wraps all content sections for correct layout */}
          <Grid container spacing={4}>
            {/* Profile Header */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                  borderRadius: 2,
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={4} md={3}>
                    <Avatar
                      src={doctor.image}
                      sx={{
                        width: { xs: 120, sm: 150, md: 180 },
                        height: { xs: 120, sm: 150, md: 180 },
                        margin: '0 auto',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8} md={9}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography
                        variant={isMobile ? "h4" : "h3"}
                        component="h1"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mr: 1,
                          mb: 1,
                        }}
                      >
                        {doctorData.name}
                      </Typography>
                      {doctor.linkedin && (
                        <Tooltip title={t('doctorProfile.tooltips.linkedin')}>
                          <IconButton
                            component={Link}
                            href={doctor.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                            sx={{ mb: 1, mx: 0.5, p: 0.5 }}
                          >
                            <LinkedInIcon sx={{ fontSize: iconSize }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {doctor.instagram && (
                        <Tooltip title={t('doctorProfile.tooltips.instagram', 'View Instagram')}>
                          <IconButton
                            component={Link}
                            href={doctor.instagram}
                            color="primary"
                            sx={{ mb: 1, mx: 0.5, p: 0.5 }}
                          >
                            {FaInstagram({ size: iconSize }) as unknown as JSX.Element}
                          </IconButton>
                        </Tooltip>
                      )}
                      {doctor.wechatQrCode && (
                        <Tooltip title={t('doctorProfile.tooltips.wechat')}>
                          <IconButton
                            onClick={handleOpenWeChatModal}
                            color="primary"
                            sx={{ mb: 1, mx: 0.5, p: 0.5 }}
                          >
                            {FaWeixin({ size: iconSize }) as unknown as JSX.Element}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      color="primary"
                      gutterBottom
                      sx={{ mb: 2 }}
                    >
                      {doctorData.role}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Detailed Description Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                   {t('doctorProfile.sections.about', { name: doctorData.name.split(',')[0] })}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-line',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                  }}
                >
                  {doctorData.detailedDescription}
                </Typography>
              </Paper>
            </Grid>
            
            {/* <<< NEW: Qualifications & Affiliations Section >>> */}
            {doctor.qualifications && doctor.qualifications.length > 0 && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={{ xs: 3, md: 5 }} justifyContent="center" alignItems="center">
                      {doctor.qualifications.map((qual, index) => (
                        <Grid item key={index} xs={4} sm={3} md={2} sx={{ textAlign: 'center' }}>
                              <Box
                                component="img"
                                src={qual.src}
                                sx={{
                                  maxWidth: '200px',
                                  width: '100%',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  filter: 'grayscale(50%)',
                                  opacity: 0.8,
                                  '&:hover': {
                                    filter: 'grayscale(0%)',
                                    opacity: 1,
                                  }
                                }}
                              />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Details Sections */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  {t('doctorProfile.sections.languages')}
                </Typography>
                <List>
                  {doctorData.languages.map((language, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <LanguageIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={language} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {doctorData.education && doctorData.education.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                    borderRadius: 2,
                    height: '100%',
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                    {t('doctorProfile.sections.education')}
                  </Typography>
                  <List>
                    {doctorData.education.map((edu, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <SchoolIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={edu} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid> {/* <<< UPDATED: Grid container now closes here */}
        </Container>

        {/* WeChat QR Code Modal */}
        <Modal
          aria-labelledby="wechat-qr-modal-title"
          aria-describedby="wechat-qr-modal-description"
          open={openWeChatModal}
          onClose={handleCloseWeChatModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={openWeChatModal} timeout={500}>
            <Box sx={modalStyle}>
              <Typography id="wechat-qr-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                {t('doctorProfile.wechatModal.title')}
              </Typography>
              <img
                src={doctor.wechatQrCode}
                alt={t('doctorProfile.wechatModal.altText', { name: doctorData.name })}
                style={{
                  width: '200px',
                  height: '200px',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
              <Typography id="wechat-qr-modal-description" sx={{ mt: 2, color: 'text.secondary' }}>
                {t('doctorProfile.wechatModal.instructions')} 
              </Typography>
            </Box>
          </Fade>
        </Modal>
      </Box>

      {/* Floating Language Switch Widget */}
      <Button
        variant="contained"
        color="inherit"
        aria-label="switch language"
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderRadius: '12px',
          minWidth: 'auto',
          px: 2,
          py: 1,
          bgcolor: 'white',
          color: 'text.primary',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            bgcolor: 'grey.100',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ fontSize: '16px' }}>
            {currentLanguage === 'en' ? '🇺🇸' : '🇨🇳'}
          </span>
          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
            {currentLanguage === 'en' ? 'EN' : 'ZH'}
          </span>
        </Box>
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <MenuItem onClick={() => handleLanguageSelect('en')} sx={{ minWidth: '120px' }}>
          <MenuListItemIcon>
            <span style={{ fontSize: '20px' }}>🇺🇸</span>
          </MenuListItemIcon>
          <MenuListItemText primary="English" />
        </MenuItem>
        <MenuItem onClick={() => handleLanguageSelect('zh')} sx={{ minWidth: '120px' }}>
          <MenuListItemIcon>
            <span style={{ fontSize: '20px' }}>🇨🇳</span>
          </MenuListItemIcon>
          <MenuListItemText primary="中文" />
        </MenuItem>
      </Menu>
    </div>
  );
};

export default DoctorProfile;